import React from 'react'
import { SEODataNames } from '../../../constants/seoDataNames.constants'
import * as env from '../../../constants/environment.constants'
import { Helmet } from 'react-helmet'
import { delocalizeUrl } from '../../../utils/functions.utils'

type SEOProps = {
  path?: string
  seoMetadata?: any
  slug?: string
  locale?: string
  isPaginated?: boolean
  pageNum?: number
  isLastPage?: boolean
  hrefForPage?: any
  siteSection?:
    | 'Product'
    | 'Partners'
    | 'Homepage'
    | 'Flex'
    | 'Resource Demo'
    | 'PDF'
    | 'Solutions'
    | 'Customer Story'
    | 'Product Download'
    | 'Resource'
    | 'Legal Page'
}

const SEOTags = (props: SEOProps) => {
  const environment = process.env.GATSBY_ENVIRONMENT

  let canonicalURL = 'https://www.uipath.com'
  let slug = ''

  if (props.slug) {
    slug = props.slug
  }

  // Set default canonical
  if (props.path) {
    const newPath = props.path[0] === '/' ? props.path : `/${props.path}`
    canonicalURL = `https://www.uipath.com${newPath}`
  } else if (slug) {
    const newSlug = slug[0] === '/' ? slug : `/${slug}`
    canonicalURL = `https://www.uipath.com${newSlug}`
  }

  // Meta Robots
  const defaultRobotsTag = (
    <meta name="robots" content="index, follow" key="robots-tag" />
  )

  // OG image data
  const defaultTwitterImage = (
    <meta
      name="twitter:image"
      content="https://images.ctfassets.net/5965pury2lcm/1OVj4PfkzVc4Vrm3IuDdAP/f415d995e5a286ddb2644cabafeb9578/UiPath-og-image-orange.png"
      key="twitter-image-tag"
    />
  )
  const defaultOGImage = (
    <meta
      property="og:image"
      content="https://images.ctfassets.net/5965pury2lcm/1OVj4PfkzVc4Vrm3IuDdAP/f415d995e5a286ddb2644cabafeb9578/UiPath-og-image-orange.png"
      key="og-image-tag"
    />
  )

  // Description data
  const defaultDescriptionTag = (
    <meta
      name="description"
      content="UiPath is a leading Robotic Process Automation vendor providing a complete software platform to help organizations efficiently automate business processes."
      key="description-tag"
    />
  )
  const defaultOGDescription = (
    <meta
      property="og:description"
      content="UiPath is a leading Robotic Process Automation vendor providing a complete software platform to help organizations efficiently automate business processes."
      key="og-decription-tag"
    />
  )
  const defaultTwitterDescription = (
    <meta
      name="twitter:description"
      content="UiPath is a leading Robotic Process Automation vendor providing a complete software platform to help organizations efficiently automate business processes."
      key="twitter-description-tag"
    />
  )

  // Title Data
  const defaultTitleTag = (
    <title key="title-tag">Robotic Process Automation | UiPath</title>
  )
  const defaultOGTitle = (
    <meta
      property="og:title"
      content="Robotic Process Automation | UiPath"
      key="og-title-tag"
    />
  )
  const defaultTwitterTitle = (
    <meta
      name="twitter:title"
      content="Robotic Process Automation | UiPath"
      key="twitter-title-tag"
    />
  )

  const defaultLang = <html lang="en" key="html-lang-tag" />

  let dynamicSEOTags: any = [
    defaultRobotsTag,
    defaultDescriptionTag,
    defaultOGDescription,
    defaultTwitterDescription,
    defaultTitleTag,
    defaultOGTitle,
    defaultTwitterTitle,
    defaultOGImage,
    defaultTwitterImage,
    defaultLang,
  ]

  let overrideCanonical

  if (props.seoMetadata) {
    const seoData = props.seoMetadata
    // Canonical URL
    if (seoData[SEODataNames.CANONICAL_URL]) {
      overrideCanonical = seoData[SEODataNames.CANONICAL_URL]
    }

    if (environment !== env.ENVIRONMENT_MASTER) {
      dynamicSEOTags[0] = (
        <meta name="robots" content="noindex, nofollow" key="robots-tag" />
      )
    } else if (seoData[SEODataNames.PAGE_META_ROBOTS]) {
      dynamicSEOTags[0] = (
        <meta
          name="robots"
          content={seoData[SEODataNames.PAGE_META_ROBOTS]}
          key="robots-tag"
        />
      )
    }

    // Descriptions
    if (
      seoData[SEODataNames.PAGE_META_DESCRIPTION] &&
      !seoData[SEODataNames.PAGE_META_DESCRIPTION].includes(
        `undefined-${props.locale}`
      )
    ) {
      dynamicSEOTags[1] = (
        <meta
          name="description"
          content={seoData[SEODataNames.PAGE_META_DESCRIPTION]}
          key="description-tag"
        />
      )
      dynamicSEOTags[2] = (
        <meta
          property="og:description"
          content={seoData[SEODataNames.PAGE_META_DESCRIPTION]}
          key="og-description-tag"
        />
      )
      dynamicSEOTags[3] = (
        <meta
          name="twitter:description"
          content={seoData[SEODataNames.PAGE_META_DESCRIPTION]}
          key="twitter-description-tag"
        />
      )
    }

    // Titles
    if (
      seoData[SEODataNames.PAGE_TITLE] &&
      !seoData[SEODataNames.PAGE_TITLE].includes(`undefined-${props.locale}`)
    ) {
      let titleToDisplay = seoData[SEODataNames.PAGE_TITLE]
      if (props.isPaginated && props.pageNum) {
        //Results page 2
        const currentPage = props.pageNum
        if (currentPage > 1) {
          titleToDisplay = `Results page ${currentPage} for ${
            seoData[SEODataNames.PAGE_TITLE]
          }`
        }
      }
      dynamicSEOTags[4] = <title key="title-tag">{titleToDisplay}</title>
      dynamicSEOTags[5] = (
        <meta property="og:title" content={titleToDisplay} key="og-title-tag" />
      )

      dynamicSEOTags[6] = (
        <meta
          name="twitter:title"
          content={titleToDisplay}
          key="twitter-title-tag"
        />
      )
    }

    // Images
    if (
      seoData[SEODataNames.OG_TWITTER_IMAGE] &&
      seoData[SEODataNames.OG_TWITTER_IMAGE].file &&
      seoData[SEODataNames.OG_TWITTER_IMAGE].file.url
    ) {
      dynamicSEOTags[7] = (
        <meta
          property="og:image"
          content={`https:${seoData[SEODataNames.OG_TWITTER_IMAGE].file.url}`}
          key="og-image-tag"
        />
      )
      dynamicSEOTags[8] = (
        <meta
          name="twitter:image"
          content={`https:${seoData[SEODataNames.OG_TWITTER_IMAGE].file.url}`}
          key="twitter-image-tag"
        />
      )
    }

    // JSON LD Data
    if (
      seoData[SEODataNames.SCHEMA_ORG] &&
      seoData[SEODataNames.SCHEMA_ORG].internal &&
      seoData[SEODataNames.SCHEMA_ORG].internal.content
    ) {
      dynamicSEOTags.push(
        <script id="uipath_schema" type="application/ld+json" key="schema-tag">
          {seoData[SEODataNames.SCHEMA_ORG].internal.content}
        </script>
      )
    }
  }

  if (environment == env.ENVIRONMENT_MASTER) {
    // SpeedCurve
    // DO NOT RANDOMIZE - only add the the 5 pages we are testing in production
    // homepage, business partners home,  tech allianes home, product page -robots, & careers homepage
    // This will remain consistant from each build
    if (
      canonicalURL === 'https://www.uipath.com/product/robots' ||
      canonicalURL === 'https://www.uipath.com/partners/buiness-partners' ||
      canonicalURL === 'https://www.uipath.com/partners/technology-alliances' ||
      canonicalURL === 'https://www.uipath.com/company/careers' ||
      canonicalURL === 'https://www.uipath.com'
    ) {
      dynamicSEOTags.push(
        <script key="lux-tag">
          {` LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};`}
        </script>
      )
      dynamicSEOTags.push(
        <script
          src="https://cdn.speedcurve.com/js/lux.js?id=819230390"
          async
          defer
          crossOrigin="anonymous"
          key="speedcurve-tag"
        ></script>
      )
    }

    // VWO Async script to enable SmartCode
    if (props.seoMetadata && props.seoMetadata.vwoScriptEnabled) {
      dynamicSEOTags.push(
        <script type="text/javascript">
          {`window._vwo_code = window._vwo_code || (function(){
          var account_id=513762,
          settings_tolerance=2000,
          library_tolerance=2500,
          use_existing_jquery=false,
          is_spa=1,
          hide_element='body',

          /* DO NOT EDIT BELOW THIS LINE */
          f=false,d=document,code={use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById('_vis_opt_path_hides');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement('script');b.src=a;b.type='text/javascript';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName('head')[0].appendChild(b);},init:function(){
          window.settings_timer=setTimeout(function () {_vwo_code.finish() },settings_tolerance);var a=d.createElement('style'),b=hide_element?hide_element+'{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}':'',h=d.getElementsByTagName('head')[0];a.setAttribute('id','_vis_opt_path_hides');a.setAttribute('type','text/css');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+(+is_spa)+'&r='+Math.random());return settings_timer; }};window._vwo_settings_timer = code.init(); return code; }());`}
        </script>
      )
    }
  }

  if (
    slug &&
    ((props.siteSection && props.siteSection === 'Product') ||
      props.siteSection === 'Homepage' ||
      props.siteSection === 'Partners')
  ) {
    const delocalizedUrl = delocalizeUrl(slug)
    dynamicSEOTags.push(
      <link
        rel="alternate"
        hreflang="en"
        key="hreflang-en-tag"
        href={`https://www.uipath.com${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="ja"
        key="hreflang-ja-tag"
        href={`https://www.uipath.com/ja${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="fr"
        key="hreflang-fr-tag"
        href={`https://www.uipath.com/fr${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="de"
        key="hreflang-de-tag"
        href={`https://www.uipath.com/de${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="es"
        key="hreflang-es-tag"
        href={`https://www.uipath.com/es${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="ko"
        key="hreflang-ko-tag"
        href={`https://www.uipath.com/ko${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />,
      <link
        rel="alternate"
        hreflang="pt"
        key="hreflang-pt-tag"
        href={`https://www.uipath.com/pt${
          delocalizedUrl[0] === '/' ? delocalizedUrl : `/${delocalizedUrl}`
        }`}
      />
    )
  }

  if (props.siteSection && props.siteSection === 'Homepage') {
    dynamicSEOTags.push(
      <meta
        name="yandex-verification"
        content="f49ba49361596a26"
        key="yandex-tag"
      />,
      <meta
        name="google-site-verification"
        content="VXIXsEi0jYxHSSnd9IYLsjZg4BDODNvsjAGzz3m7crQ"
        key="yandex-tag"
      />,
      <meta
        name="google-site-verification"
        key="google-site-verification-tag1"
        content="zUwxV252NIeRj4nPxXtNm5kqtTdUhXiehgKf0L8RO2c"
      />,
      <meta
        name="google-site-verification"
        key="google-site-verification-tag2"
        content="scTtRs-w5M99W1v6htzS4TBtCghf3TZC8EIELE-Il30"
      />,
      <meta
        name="google-site-verification"
        key="google-site-verification-tag3"
        content="vA2-EverxvUVHD0_hZJ9zAV2VQjf9CPrW9sLz2D6jw4"
      />,
      <meta
        name="ahrefs-site-verification"
        key="google-site-verification-tag4"
        content="004c3735e4266c8b7c3dc74fdc5a80bfd0a409348c8243572e54bbad2888fd08"
      />,
      <meta
        name="msvalidate.01"
        content="800F6CC4D7045B6406628122C74197EE"
        key="msvalidate-tag"
      />,
      <meta
        name="published-date"
        content="November 17, 2020 2 PM"
        key="published-date-tag"
      />,
      <meta
        name="updated-date"
        key="updated-date-tag"
        content="April 16, 2021 6 PM"
      />
    )
  }

  if (props.isPaginated && props.pageNum) {
    const currentPage = props.pageNum

    let prevHref, nextHref
    let seoLinks
    if (props.hrefForPage) {
      prevHref = props.hrefForPage(currentPage - 1)
      nextHref = props.hrefForPage(currentPage + 1)
    }

    if (currentPage === 1) {
      // First page
      seoLinks = <link rel="next" href={nextHref} />
    } else if (props.isLastPage) {
      // Last page
      seoLinks = <link rel="prev" href={prevHref} />
    } else if (currentPage > 1) {
      // Pages from 1 to second to last page
      seoLinks = [
        <link rel="prev" href={prevHref} />,
        <link rel="next" href={nextHref} />,
      ]
    }

    dynamicSEOTags.push(seoLinks)
  }

  return (
    <Helmet
      link={
        overrideCanonical
          ? [
              {
                rel: 'canonical',
                key: overrideCanonical,
                href: overrideCanonical,
              },
            ]
          : []
      }
    >
      {dynamicSEOTags}
      <meta property="og:type" content="page"></meta>
      <meta property="og:image:type" content="image/jpg" />

      <meta name="twitter:site" content="@uipath"></meta>
      <meta name="twitter:domain" content="www.uipath.com"></meta>

      <meta property="fb:app_id" content="2034514640109878" />

      <meta charSet="utf-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
      <meta name="author" content="UiPath Inc." />
      <link
        rel="shortcut icon"
        href="https://images.ctfassets.net/5965pury2lcm/4425FsZbwrsJdhttHkPlTE/61068f00208417f2017bce78bf2114b4/favicon-32x32.png"
      />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1"
      />
      <meta
        name="google-site-verification"
        content="uftQEsVNL_29Jh2NycRtYUtHEobYF30uDVYkqg_2Ju4"
      />
      <meta content="summary_large_image" name="twitter:card" />
      <meta name="theme-color" content="#131d40" />
      <meta name="alice" content="ed650dee-c9d2-4e5e-85af-4c4df1916b8c" />
    </Helmet>
  )
}
export default SEOTags
